import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { XMarkIcon, PlusIcon, MinusIcon, CalendarIcon, ClockIcon } from "@heroicons/react/24/outline";
import { assignBuddies, getAssignedBuddies, getEmployeesFromGraphApi } from "../../api/offeredcandidate.api"
import useApiToken from "../../hooks/useApiToken";

type BuddyAssignment = {
  id: string;
  name: string | null;
  email: string | null;
};

type OnBoardingBuddyModalProps = {
  isOpen: boolean;
  offerId: string;
  candidateName: string;
  candidateEmail: string;
  commencementDate: Date;
  onClose: () => void;
  onSuccess: () => void;
};
type employeeType = {
  id: string,
  name: string,
  email: string
}


const OnBoardingBuddyModal: React.FC<OnBoardingBuddyModalProps> = ({
  isOpen,
  offerId,
  candidateName,
  candidateEmail,
  commencementDate,
  onClose,
  onSuccess,
}) => {
  const token = useApiToken();
  const [meetingDate, setMeetingDate] = useState<Date | null>(commencementDate);
  const [meetingTime, setMeetingTime] = useState<Date | null>(null);
  const [buddyAssignments, setBuddyAssignments] = useState<BuddyAssignment[]>([
    { id: Math.random().toString(), name: null, email: null },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [employees, setEmployees] = useState<employeeType[]>([]);
  const [isLoading, setIsLoading] = useState<Boolean>(false)
  // Keep track of already assigned buddies fetched from the server
  const [preAssignedBuddies, setPreAssignedBuddies] = useState<BuddyAssignment[]>([]);

  if (!isOpen) return null;

  const addBuddySlot = () => {
    setBuddyAssignments([...buddyAssignments, { id: Math.random().toString(), name: null, email: null }]);
  };
  const fetchEmployeesFromGraphApi = async () => {
    setIsLoading(true);
    try {
      const result = await getEmployeesFromGraphApi(token!);
      if (Array.isArray(result)) {
        setEmployees(result);
      } else {
        setEmployees([]);
        setError("Failed to fetch employees: Invalid data format");
      }
    } catch (err: any) {
      setEmployees([]);
      setError(
        err?.message ||
        "Failed to fetch employees from Graph API"
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployeesFromGraphApi();
  }, [])



  const removeBuddySlot = (id: string) => {
    if (buddyAssignments.length > 1) {
      setBuddyAssignments(buddyAssignments.filter((b) => b.id !== id));
    }
  };

  const updateBuddy = (id: string, buddyId: string) => {
    const selectedBuddy = employees.find(e => e.id === buddyId);
    setBuddyAssignments(
      buddyAssignments.map((b) =>
        b.id === id
          ? {
            ...b,
            id: buddyId,
            name: selectedBuddy?.name || null,
            email: selectedBuddy?.email || null,
          }
          : b
      )
    );
  };

  const handleClose = () => {
    setMeetingDate(null);
    setMeetingTime(null);
    setBuddyAssignments([{ id: Math.random().toString(), name: null, email: null }]);
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);
    if (!meetingDate) {
      setError("Please select date");
      return;
    }


    const selectedBuddies = buddyAssignments
      .filter((b) => b.email != null && b.name != null)
      .map((b) => ({
        id: b.id,
        name: b.name,
        email: b.email
      }));


    if (selectedBuddies.length === 0) {
      setError("Please select at least one buddy");
      return;
    }

    try {
      setIsSubmitting(true);
      await assignBuddies({
        offerId: offerId,
        buddies: selectedBuddies,
        meetingDate: meetingDate,
      }, token!);
      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.message || "Failed to assign buddies");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white dark:bg-darkbg rounded-xl shadow-2xl max-w-lg w-full my-8 overflow-hidden border border-transparent dark:border-gray-700">
        {/* Modal Header */}
        <div className="bg-white dark:bg-darkbglight border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-200">Assign Onboarding Buddy</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">For {candidateName} ({candidateEmail})</p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Date */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <CalendarIcon className="h-4 w-4" /> Onbarding Date
              </label>
              <DatePicker
                selected={meetingDate ?? commencementDate}
                onChange={(date) => setMeetingDate(date)}
                minDate={new Date()}
                placeholderText="Select date"
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkbglight text-gray-900 dark:text-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              />
            </div>

          </div>

          <hr className="border-gray-100 dark:border-gray-700" />

          {/* Buddy Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Select Buddies</label>
            {buddyAssignments.map((assignment, index) => (
              <div key={assignment.id} className="flex items-center gap-2">
                <div className="flex-1">
                  <select
                    value={assignment.id || ""}
                    onChange={(e) => updateBuddy(assignment.id, e.target.value)}
                    className="w-full bg-white dark:bg-darkbglight border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-300 px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    disabled={isSubmitting}
                  >
                    <option value="">Select an employee...</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name} ({emp.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-1">
                  {buddyAssignments.length > 1 && (
                    <button
                      onClick={() => removeBuddySlot(assignment.id)}
                      className="p-2 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors border border-transparent hover:border-red-100 dark:hover:border-red-800"
                    >
                      <MinusIcon className="h-5 w-5" />
                    </button>
                  )}
                  {/* {index === buddyAssignments.length - 1 && (
                    <button
                      onClick={addBuddySlot}
                      className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 rounded-lg transition-colors border border-transparent hover:border-blue-100 dark:hover:border-blue-800"
                    >
                      <PlusIcon className="h-5 w-5" />
                    </button>
                  )} */}
                </div>
              </div>
            ))}
          </div>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-800 text-red-600 dark:text-red-300 text-sm rounded-lg">
              {error}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="bg-gray-50 dark:bg-darkbglight px-6 py-4 flex justify-end gap-3 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-6 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 shadow-sm transition-all"
          >
            {isSubmitting ? "Assigning..." : "Assign Buddy"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnBoardingBuddyModal;
